import omit from "lodash/omit";
import some from "lodash/some";
import uniq from "lodash/uniq";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { SimplePagination } from "PFComponents/pagination/simple_pagination";
import { Typography } from "PFComponents/typography";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useCloseVacancies, useReopenVacancy } from "PFCore/hooks/queries/activity";
import { ClosureReasonKey, VacanciesCollection, Vacancy as VacancyType } from "PFTypes";
import { Id } from "PFTypes/id";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { ActivitySummary } from "../../activity_summary/activity_summary";
import css from "./activity_vacancies.module.scss";
import { Vacancy } from "./components/vacancy";
import { useActivityPageVacancies } from "./hooks/use_activity_page_vacancies";
import { useVacancyProfile } from "./hooks/use_vacancy_profile";

const CLOSE_VACANCIES_SUCCESS_ACTION_ID = getGrowlId("manager-feedback-form");

const getVacancyNumber = (meta: VacanciesCollection["meta"], index: number): number =>
  (meta.perPage || 5) * ((meta.page || 1) - 1) + index + 1;

export const ActivityVacancies = () => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("activities", { keyPrefix: "show.vacancies" });
  const { t: tTabs } = useTranslation("activities");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const activity = useCurrentActivity();
  const { vacancies, changePage } = useActivityPageVacancies(activity);
  const { getVacancyProfiles, getVacancyFilledRole, isFetching } = useVacancyProfile(vacancies);

  const { mutate: closeVacancies } = useCloseVacancies();
  const { mutate: reopenVacancy } = useReopenVacancy();

  const showLoadingDots = !vacancies || isFetching;
  const vacanciesMeta = vacancies?.meta;

  const [selectedReasons, setSelectedReasons] = useState<Record<Id, string>>({});
  const [selectedCandidates, setSelectedCandidates] = useState<Record<Id, string>>({});
  const [displayConfirmModal, setDisplayConfirmModal] = useState(false);
  const [selectedVacancyId, setSelectedVacancyId] = useState<Id | null>(null);
  const { getFirstError } = useResponseErrors();

  const selectedCount = Object.keys(selectedReasons).length;

  const closureReasons = useMemo(() => vacanciesMeta?.availableClosureReasons || [], [vacanciesMeta]);

  const handleCloseVacancies = () => {
    closeVacancies(
      { selectedReasons, closureDetails: selectedCandidates, activityId: activity.id },
      {
        onSuccess: (response: PromiseSettledResult<VacancyType>[]) => {
          setSelectedReasons({});
          setSelectedCandidates({});
          const fulfilledPromises = response.filter(
            ({ status }) => status === "fulfilled"
          ) as PromiseFulfilledResult<VacancyType>[];
          const rejectedPromises = response.filter(
            ({ status }) => status === "rejected"
          ) as PromiseRejectedResult[];

          if (fulfilledPromises.length > 0) {
            showGrowl({
              id: CLOSE_VACANCIES_SUCCESS_ACTION_ID,
              kind: "success",
              message: t("vacancyHasBeenClosed", { count: fulfilledPromises.length }),
              hidePreviousByTypeAndId: true,
              ttl: 5000
            });
          }

          if (rejectedPromises.length > 0) {
            const errorMessages = uniq(rejectedPromises.map(({ reason }) => getFirstError(reason)));

            errorMessages.forEach((message: string) => {
              showGrowl({
                message,
                kind: "error"
              });
            });
          }
        }
      }
    );
  };

  const handleReopenVacancies = (vacancyId: Id) => {
    reopenVacancy(
      {
        activityId: activity.id,
        vacancyId
      },
      {
        onSuccess: () => {
          setSelectedVacancyId(null);
          navigate(`/activities/${id}/edit`);
        }
      }
    );
  };

  const isAutoCompleteReasonNotFilled = useMemo(
    () =>
      some(selectedReasons, (reasonValue, vacancyId) => {
        const reason = closureReasons.find(({ key }) => key === reasonValue);
        return (
          !!reason &&
          reason.key === ClosureReasonKey.FilledWithNamedResource &&
          !selectedCandidates[vacancyId]
        );
      }),
    [closureReasons, selectedCandidates, selectedReasons]
  );

  const selectedProfileIds = useMemo(
    () =>
      Object.entries(selectedReasons)
        .filter(
          ([vacancyId, reasonValue]) =>
            reasonValue === ClosureReasonKey.FilledWithNamedResource && !!selectedCandidates[vacancyId]
        )
        .map(([vacancyId]) => selectedCandidates[vacancyId]) as Id[],
    [selectedCandidates, selectedReasons]
  );

  return (
    <Inline
      gap="spacingXl"
      className={css.activityVacancies}
      alignItems="start"
      role="tabpanel"
      aria-label={tTabs("show.tabs.vacancies")}
      qaId="ActivityPage.vacanciesTab"
    >
      <ActivitySummary />
      {showLoadingDots && <LoadingDots circlesEnabled pageViewEnabled />}
      {!showLoadingDots && (
        <Stack
          gap="spacingLg"
          data-qa-id="ActivityVacanciesTabVacanciesContainer"
          className={css.activityVacanciesRightSide}
        >
          <Inline spread alignItems="center">
            <Typography variant="labelRegular">
              {t("vacanciesCount", { count: vacancies.meta.total })}
            </Typography>
            <Button
              text={t("closeVacancy", { count: selectedCount })}
              disabled={selectedCount === 0 || isAutoCompleteReasonNotFilled}
              onClick={handleCloseVacancies}
            />
          </Inline>
          {vacancies.entries.map((vacancy, index) => (
            <Vacancy
              key={vacancy.id}
              vacancyNumber={getVacancyNumber(vacanciesMeta!, index)}
              closureReasons={closureReasons}
              reasonValue={selectedReasons[vacancy.id]}
              candidateValue={selectedCandidates[vacancy.id]}
              vacancy={vacancy}
              selectedProfileIds={selectedProfileIds}
              profiles={getVacancyProfiles(vacancy)}
              filledRole={getVacancyFilledRole(vacancy)}
              onReasonChange={(value: string) => {
                const oldReason = closureReasons.find(({ key }) => key === selectedReasons[vacancy.id]);
                const newReason = closureReasons.find(({ key }) => key === value);
                if (
                  oldReason &&
                  newReason &&
                  (oldReason.key === ClosureReasonKey.FilledWithNamedResource) !==
                    (newReason.key === ClosureReasonKey.FilledWithNamedResource)
                ) {
                  setSelectedCandidates(omit(selectedCandidates, vacancy.id));
                }
                setSelectedReasons({ ...selectedReasons, [vacancy.id]: value });
              }}
              onCandidateChange={(selected: string | number | null) => {
                setSelectedCandidates({ ...selectedCandidates, [vacancy.id]: String(selected) });
              }}
              onReopenVacancy={() => {
                setSelectedVacancyId(vacancy.id);
                setDisplayConfirmModal(true);
              }}
            />
          ))}
          {vacancies.entries.length > 0 && (
            <SimplePagination
              currentPage={vacanciesMeta!.page || 1}
              handleClick={changePage}
              totalPages={vacanciesMeta!.totalPages}
            />
          )}
        </Stack>
      )}
      {displayConfirmModal && (
        <Modal
          title={t("reopenVacancy")}
          onOK={() => {
            handleReopenVacancies(selectedVacancyId!);
          }}
          onClose={() => {
            setDisplayConfirmModal(false);
            setSelectedVacancyId(null);
          }}
        >
          {t("wantToReopenVacancy")}
        </Modal>
      )}
    </Inline>
  );
};
